import {
  InAppIcon,
  ClickIcon,
  EyeIcon,
  MailClickIcon,
  MailIcon,
  MailOpenIcon,
  MailSendIcon,
  MobilePhoneIcon,
  PushClickedIcon,
  PushSentIcon,
  PushTappedIcon,
  SendMessageIcon,
  SmsIcon,
  SmsOpenedIcon,
  SmsSentIcon,
  SmsTappedIcon,
  WhatsappLineIcon,
} from "@hightouch/htui";

import { ChartGranularity } from "src/components/charts/chart-utils";
import type { FilterOptionOrDisabled } from "src/components/folders/filters";
import { formatDateLong, formatRelativeShort } from "src/utils/time";

import {
  ChannelType,
  type VolumeCapReset,
  type SendRatePeriod,
  type DurationUnit,
} from "src/types/cep";
import { DerivedCampaignStatus } from "src/graphql";
import type { CampaignType } from "src/cep/campaigns/campaign-detail/form-types";
import type { JourneyState } from "src/pages/journeys/types";
import { EngagementMetric, getSoleChannel } from "./types";

export type StatsTimeRange = "7d" | "30d" | "all";

/** Why an editing control is off when the surface's `content.canEdit` is false. */
export const READ_ONLY_CONTENT_MESSAGE = "This content is read-only";

/** The import dialog's warning, shared by every editor whose host opens an import in the Agent editor. */
export const IMPORT_TO_AGENT_EDITOR_TITLE =
  "This will switch you to the Agent editor";
export const IMPORT_WITH_DYNAMIC_CONTENT_NOTE =
  "An email with dynamic content opens in the HTML editor instead.";

export const STATS_TIME_RANGE_CONFIG: Record<
  StatsTimeRange,
  { days: number; subtitle: string; granularity: ChartGranularity }
> = {
  "7d": {
    days: 7,
    subtitle: "All messages, Last 7 days",
    granularity: ChartGranularity.SevenDays,
  },
  "30d": {
    days: 30,
    subtitle: "All messages, Last 30 days",
    granularity: ChartGranularity.ThirtyDays,
  },
  all: {
    days: 365 * 10,
    subtitle: "All messages, All time",
    granularity: ChartGranularity.NinetyDays,
  },
};

export const MAX_TEXT_AREA_LENGTH = 140;

export const CHANNELS: Record<
  ChannelType,
  { label: string; icon: React.ComponentType }
> = {
  [ChannelType.Email]: {
    label: "Email",
    icon: MailIcon,
  },
  [ChannelType.Sms]: {
    label: "SMS",
    icon: SmsIcon,
  },
  [ChannelType.Whatsapp]: {
    label: "WhatsApp",
    icon: WhatsappLineIcon,
  },
  [ChannelType.Push]: {
    label: "Push",
    icon: MobilePhoneIcon,
  },
  [ChannelType.InApp]: {
    label: "In-app",
    icon: InAppIcon,
  },
};

export const isKnownChannel = (channel: string): channel is ChannelType =>
  Object.hasOwn(CHANNELS, channel);

/** How each channel reads mid-sentence, as in "Create a new email campaign". */
export const CHANNEL_NOUNS: Record<ChannelType, string> = {
  [ChannelType.Email]: "email",
  [ChannelType.Sms]: "SMS",
  [ChannelType.Whatsapp]: "WhatsApp",
  [ChannelType.Push]: "push",
  [ChannelType.InApp]: "in-app",
};

type ChannelOption = {
  value: ChannelType;
  label: string;
  icon: React.ComponentType;
  isDisabled?: boolean;
};

export const CHANNEL_OPTIONS: ChannelOption[] = Object.entries(CHANNELS).map(
  ([value, { label, icon }]) => ({
    value: value as ChannelType,
    label,
    icon,
  }),
);

export const ChannelIconMapping: Record<ChannelType, React.ComponentType> = {
  [ChannelType.Email]: MailIcon,
  [ChannelType.Sms]: SmsIcon,
  [ChannelType.Whatsapp]: WhatsappLineIcon,
  [ChannelType.Push]: MobilePhoneIcon,
  [ChannelType.InApp]: InAppIcon,
};

export const METRIC_ICONS: Record<
  ChannelType,
  Record<EngagementMetric, React.ComponentType>
> = {
  [ChannelType.Email]: {
    [EngagementMetric.Sent]: MailSendIcon,
    [EngagementMetric.Delivered]: MailSendIcon,
    [EngagementMetric.Opened]: MailOpenIcon,
    [EngagementMetric.Clicked]: MailClickIcon,
  },
  [ChannelType.Sms]: {
    [EngagementMetric.Sent]: SmsSentIcon,
    [EngagementMetric.Delivered]: SmsSentIcon,
    [EngagementMetric.Opened]: SmsOpenedIcon,
    [EngagementMetric.Clicked]: SmsTappedIcon,
  },
  [ChannelType.Whatsapp]: {
    [EngagementMetric.Sent]: SmsSentIcon,
    [EngagementMetric.Delivered]: SmsSentIcon,
    [EngagementMetric.Opened]: SmsOpenedIcon,
    [EngagementMetric.Clicked]: SmsTappedIcon,
  },
  [ChannelType.Push]: {
    [EngagementMetric.Sent]: PushSentIcon,
    [EngagementMetric.Delivered]: PushSentIcon,
    [EngagementMetric.Opened]: PushTappedIcon,
    [EngagementMetric.Clicked]: PushClickedIcon,
  },
  [ChannelType.InApp]: {
    [EngagementMetric.Sent]: InAppIcon,
    [EngagementMetric.Delivered]: InAppIcon,
    [EngagementMetric.Opened]: EyeIcon,
    [EngagementMetric.Clicked]: ClickIcon,
  },
};

// A mixed or unknown channel set (a journey's "All messages" scope spanning
// email + SMS) has no honest channel icon.
const NEUTRAL_METRIC_ICONS: Record<EngagementMetric, React.ComponentType> = {
  [EngagementMetric.Sent]: SendMessageIcon,
  [EngagementMetric.Delivered]: SendMessageIcon,
  [EngagementMetric.Opened]: EyeIcon,
  [EngagementMetric.Clicked]: ClickIcon,
};

export function getMetricIcon(
  channels: ChannelType | ChannelType[] | string | null | undefined,
  metric: EngagementMetric,
): React.ComponentType {
  const channel = getSoleChannel(channels);
  return channel ? METRIC_ICONS[channel][metric] : NEUTRAL_METRIC_ICONS[metric];
}

export const DEFAULT_COOLDOWN_HOURS = 168;

export const VOLUME_CAP_RESET_OPTIONS: {
  label: string;
  value: VolumeCapReset;
  description: string;
}[] = [
  {
    label: "for the campaign lifetime",
    value: "lifetime",
    description: "One limit for the entire campaign",
  },
  {
    label: "per scheduled send",
    value: "per_run",
    description: "Each send gets its own limit",
  },
];

export const SEND_RATE_PERIOD_OPTIONS: {
  label: string;
  value: SendRatePeriod;
}[] = [
  { label: "minute", value: "minute" },
  { label: "hour", value: "hour" },
  { label: "day", value: "day" },
];

// Why per_run is unavailable (shown as the disabled option message), or
// undefined when allowed — runless triggers have no run to reset on (CEP-1612).
export function getPerRunResetDisabledReason(
  campaignType: CampaignType | null | undefined,
): string | undefined {
  if (campaignType == null) return "Pick a schedule type first";
  switch (campaignType) {
    case "one_time":
    case "recurring":
      return undefined;
    case "api_triggered":
      return "API-triggered campaigns don't have scheduled sends";
    case "ai_decisioning":
      return "AI Decisioning campaigns send when the agent chooses this message";
    case "in_app":
      return "In-app campaigns reconcile an inbox instead of sending per run";
    default: {
      // Exhaustiveness: adding a CampaignType breaks the build here until this
      // function decides whether the new type resets per scheduled send.
      return campaignType satisfies never;
    }
  }
}

export const DURATION_UNIT_OPTIONS: {
  label: string;
  value: DurationUnit;
}[] = [
  { label: "hours", value: "hours" },
  { label: "days", value: "days" },
];

export const EMAIL_PREVIEW_MAX_WIDTH = "2xl";
export const EMAIL_PREVIEW_MIN_WIDTH = "2xl";

export const PUSH_PREVIEW_MAX_WIDTH = "744px";

export const WHATSAPP_APPROVED_CATEGORY_TOOLTIP =
  "Meta approved this template under its category, so it can't change.";
export const WHATSAPP_APPROVED_SENDER_TOOLTIP =
  "Meta approved this template for this sender, so the sender can't change.";
export const WHATSAPP_APPROVED_TOPIC_TOOLTIP =
  "Meta approved this template, so the subscription topic can't change.";
export const WHATSAPP_LIBRARY_TILE_CATEGORY_TOOLTIP =
  "This tile uses a library template, so the category can't change. Detach the template to change it.";
export const WHATSAPP_CATEGORY_MESSAGE_TYPE_TOOLTIP =
  "The WhatsApp category sets the message type.";
export const DIFFERENT_WHATSAPP_ACCOUNT_REASON =
  "This template is on a different WhatsApp account than the sender identity.";

export const MINI_THUMBNAIL_SCALE = "6.6px";
export const MINI_THUMBNAIL_LINE_HEIGHT = "8.8px";

export {
  isDraftStatus,
  EDITABLE_CAMPAIGN_STATUSES,
  isEditableCampaignStatus,
} from "@hightouch/lib/cep/surface-editability";

/**
 * Campaign states in which a message running an A/B test may show results:
 * every state past activation, live and terminal alike (an errored campaign may
 * have partial sends worth reading). Scheduled precedes any send, but the card
 * still shows so the marketer can review the test setup — it captions itself
 * "Results will appear after this campaign's first send". Draft renders its own
 * overview layout without the Performance section, and Archived hides the
 * panel deliberately.
 */
const AB_TEST_RESULT_STATUSES = new Set<DerivedCampaignStatus>([
  DerivedCampaignStatus.Scheduled,
  DerivedCampaignStatus.Activated,
  DerivedCampaignStatus.Running,
  DerivedCampaignStatus.Paused,
  DerivedCampaignStatus.Completed,
  DerivedCampaignStatus.Error,
]);

/**
 * Whether the A/B results panel may render. Only a message that actually has an
 * A/B test earns the wider window above; without one the panel stays on the
 * Activated-only gate it has always used. Shared so the message overview and
 * the campaign detail Performance section can't drift apart.
 */
export const canShowCampaignAbTestResults = (
  status: DerivedCampaignStatus | null | undefined,
  hasAbTest: boolean,
): boolean =>
  hasAbTest
    ? status != null && AB_TEST_RESULT_STATUSES.has(status)
    : status === DerivedCampaignStatus.Activated;

/**
 * The journey mirror of {@link AB_TEST_RESULT_STATUSES}: every state past
 * activation. `draft`/`staged` precede a launch; `resetting` is on its way
 * back to draft.
 */
const AB_TEST_RESULT_JOURNEY_STATES = new Set<JourneyState>([
  "active",
  "draining",
  "paused",
  "off",
  // A journey mid-deletion still has its run data until cleanup finishes.
  "deleting",
]);

export const canShowJourneyAbTestResults = (
  journeyState: JourneyState | null | undefined,
  hasAbTest: boolean,
): boolean =>
  hasAbTest &&
  journeyState != null &&
  AB_TEST_RESULT_JOURNEY_STATES.has(journeyState);

/**
 * A/B results gate for the message overview layouts. Campaign takes precedence
 * over journey, mirroring `messageSurfaceInput`; a standalone message never
 * sends, so it has no results to show.
 */
export const canShowMessageAbTestResults = (args: {
  campaignInfo: { derivedStatus: DerivedCampaignStatus | undefined } | null;
  journeyState: JourneyState | null;
  hasAbTest: boolean;
}): boolean =>
  args.campaignInfo != null
    ? canShowCampaignAbTestResults(
        args.campaignInfo.derivedStatus,
        args.hasAbTest,
      )
    : canShowJourneyAbTestResults(args.journeyState, args.hasAbTest);

/**
 * Campaign states where promoting an A/B winner is offered — states that still
 * send or are about to. Narrower than {@link AB_TEST_RESULT_STATUSES}: terminal
 * Completed/Archived and Draft are excluded, since a winner only matters while
 * variants are still going out. Promoting never ends the test, so results keep
 * showing everywhere the wider gate allows.
 */
const PROMOTE_WINNER_CAMPAIGN_STATUSES = new Set<DerivedCampaignStatus>([
  DerivedCampaignStatus.Scheduled,
  DerivedCampaignStatus.Running,
  DerivedCampaignStatus.Paused,
]);

/**
 * The journey mirror: active journeys still send (`draining` flushes users
 * already held), so a winner promotion still takes effect on future sends.
 */
const PROMOTE_WINNER_JOURNEY_STATES = new Set<JourneyState>([
  "active",
  "draining",
]);

/**
 * Whether the surface is live enough to promote an A/B winner. Campaign takes
 * precedence over journey (mirroring `canShowMessageAbTestResults`); a
 * standalone message never sends, so it can never promote. A realtime journey's
 * message is edited only on the journey canvas, so it never promotes here.
 */
export const canPromoteAbTestWinner = (args: {
  campaignStatus?: DerivedCampaignStatus | null;
  journeyState?: JourneyState | null;
  realtimeJourney?: boolean;
}): boolean =>
  args.campaignStatus != null
    ? PROMOTE_WINNER_CAMPAIGN_STATUSES.has(args.campaignStatus)
    : args.journeyState != null &&
      !args.realtimeJourney &&
      PROMOTE_WINNER_JOURNEY_STATES.has(args.journeyState);

const AB_TEST_ELEMENT_LABELS: Record<string, string> = {
  subject_preheader: "Subject + Preheader",
  body: "Content",
};

/**
 * The tested element as the marketer named it. The element arrives from the
 * database as a free-form string, so an unrecognized one falls back to itself
 * rather than rendering blank. Shared so the results card and its details
 * drawer label the same experiment identically.
 */
export const abTestElementLabel = (element: string): string =>
  AB_TEST_ELEMENT_LABELS[element] ?? element;

/**
 * The A/B filter answers which element is under test, not how far along the
 * test is — the badge deliberately carries no status, so neither does this.
 * Ids other than `none` are `message_experiments.element` values, so the
 * where-builders can match on them directly.
 */
export const AB_TEST_FILTER_NONE_ID = "none";

export const AB_TEST_FILTER_OPTIONS: FilterOptionOrDisabled[] = [
  { id: AB_TEST_FILTER_NONE_ID, label: "None", order: 0 },
  {
    id: "subject_preheader",
    label: abTestElementLabel("subject_preheader"),
    order: 1,
  },
  { id: "body", label: abTestElementLabel("body"), order: 2 },
];

/**
 * The Schedule type option for journey-triggered message rows. The other ids
 * are `campaigns.campaign_type` values; this one matches no campaign.
 */
export const JOURNEY_TRIGGERED_SCHEDULE_TYPE_ID = "journey_triggered";

/**
 * When a test ended, as the phrase that follows "ended": "today", "3h ago", or
 * "on Feb 2" past five weeks. Null when no end date was recorded.
 *
 * Never a clock time. `completed_at` is a scheduled end *date* — End test
 * stamps end-of-today — so rendering it as a timestamp would claim a precision
 * the column doesn't have (and read as 11:59 PM every time).
 */
export function abTestEndedWhen(completedAt: string | null): string | null {
  if (!completedAt) return null;
  const relative = formatRelativeShort(completedAt);
  if (!relative) return null;
  // Inside the first hour `formatRelativeShort` says "Just now", which reads as
  // a clock reading rather than a day.
  if (relative === "Just now") return "today";
  return relative.endsWith(" ago") ? relative : `on ${relative}`;
}

/**
 * How long ago a test ended, as a predicate reading off "Test …" or "Your
 * Subject A/B test …". Shared so the ended banner and the drawer's ended
 * marker can't drift onto different phrasings.
 */
export function abTestEndedPhrase(completedAt: string | null): string {
  const when = abTestEndedWhen(completedAt);
  return when ? `ended ${when}` : "has ended";
}

/**
 * The exact day a test ended, for the hover that every relative date owes. Date
 * only — `completed_at` is a scheduled end date, so a time would be invented.
 */
export function abTestEndedOn(completedAt: string | null): string | null {
  // `formatDateLong` ("August 15, 2026"), not the numeric default — a
  // month-name date can't be misread across regions.
  return completedAt ? formatDateLong(completedAt) : null;
}

/**
 * What the Winner promoted badge says on hover. Promotion itself isn't
 * timestamped — `message_experiments` stores only `promoted_variant_key` — so
 * this names the winner and its consequence, neither of which needs a date.
 */
export function abTestPromotedWinnerTooltip(variantName: string): string {
  return `"${variantName}" was promoted as winner and will be sent to all future recipients.`;
}

/**
 * Stands in for any A/B cell with no value. Shared by every formatter and row
 * below so the results card, its details-drawer table and the arm rows can't
 * drift onto different placeholders.
 */
export const EMPTY_CELL = "--";

/** Format a 0–1 rate as a percentage: whole number at ≥10%, one decimal below. */
export function formatRate(value: number | null | undefined): string {
  if (value == null) return EMPTY_CELL;
  const pct = value * 100;
  return pct >= 10 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
}

const RATE_METRIC_LABELS: Record<string, string> = {
  email_opened: "Open rate",
  email_clicked: "Click rate",
  sms_clicked: "Click rate",
  push_opened: "Open rate",
};

const SEND_RATE_TOOLTIP =
  "Uses messages sent as the denominator, matching the Engagement card.";

const SEND_RATE_METRICS: Record<string, true> = {
  email_opened: true,
  email_clicked: true,
  sms_clicked: true,
  push_opened: true,
};

export function metricRateTooltip(
  managedModelType: string | null | undefined,
): string | undefined {
  // `in` would report true for every Object.prototype member.
  if (
    managedModelType == null ||
    !Object.hasOwn(SEND_RATE_METRICS, managedModelType)
  ) {
    return undefined;
  }
  return SEND_RATE_TOOLTIP;
}

/**
 * Friendly rate label for a metric: managed models get a channel-agnostic name
 * ("Open rate"), custom metrics fall back to "<name> rate". Shared by the
 * results card and the details-drawer table so the two label metrics the same.
 */
export function metricRateLabel(
  managedModelType: string | null | undefined,
  name: string | null | undefined,
): string {
  const managed = managedModelType
    ? RATE_METRIC_LABELS[managedModelType]
    : undefined;
  return managed ?? (name ? `${name} rate` : "Rate");
}

/** Hover text on a merge tag whose variable the send can't fill. */
export const UNKNOWN_VARIABLE_MESSAGE =
  "This variable is invalid or has been deleted";
